(function (global, factory) {
    typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
    typeof define === 'function' && define.amd ? define(['exports'], factory) :
    (global = typeof globalThis !== 'undefined' ? globalThis : global || self, factory(global.xpoint = {}));
})(this, (function (exports) { 'use strict';

    /******************************************************************************
    Copyright (c) Microsoft Corporation.

    Permission to use, copy, modify, and/or distribute this software for any
    purpose with or without fee is hereby granted.

    THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
    REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
    AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
    INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
    LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
    OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
    PERFORMANCE OF THIS SOFTWARE.
    ***************************************************************************** */
    /* global Reflect, Promise */


    function __awaiter(thisArg, _arguments, P, generator) {
        function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
        return new (P || (P = Promise))(function (resolve, reject) {
            function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
            function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
            function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
            step((generator = generator.apply(thisArg, _arguments || [])).next());
        });
    }

    function __classPrivateFieldGet(receiver, state, kind, f) {
        if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
        if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
        return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
    }

    function __classPrivateFieldSet(receiver, state, value, kind, f) {
        if (kind === "m") throw new TypeError("Private method is not writable");
        if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
        if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
        return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
    }

    // Unique ID creation requires a high quality random # generator. In the browser we therefore
    // require the crypto API and do not support built-in fallback to lower quality random number
    // generators (like Math.random()).
    let getRandomValues;
    const rnds8 = new Uint8Array(16);
    function rng() {
      // lazy load so that environments that need to polyfill have a chance to do so
      if (!getRandomValues) {
        // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation.
        getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);

        if (!getRandomValues) {
          throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
        }
      }

      return getRandomValues(rnds8);
    }

    /**
     * Convert array of 16 byte values to UUID string format of the form:
     * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
     */

    const byteToHex = [];

    for (let i = 0; i < 256; ++i) {
      byteToHex.push((i + 0x100).toString(16).slice(1));
    }

    function unsafeStringify(arr, offset = 0) {
      // Note: Be careful editing this code!  It's been tuned for performance
      // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
      return (byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]]).toLowerCase();
    }

    const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
    var native = {
      randomUUID
    };

    function v4(options, buf, offset) {
      if (native.randomUUID && !buf && !options) {
        return native.randomUUID();
      }

      options = options || {};
      const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

      rnds[6] = rnds[6] & 0x0f | 0x40;
      rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

      if (buf) {
        offset = offset || 0;

        for (let i = 0; i < 16; ++i) {
          buf[offset + i] = rnds[i];
        }

        return buf;
      }

      return unsafeStringify(rnds);
    }

    class ClientNotFoundError extends Error {
        constructor() {
            super('Client not found error');
        }
    }

    class IllegalArgumentError extends Error {
        constructor(message) {
            super(message);
        }
    }

    class SdkCacheImpl {
        constructor(ttl) {
            this._cache = {};
            this.ttl = ttl;
        }
        get(key) {
            const entry = this._cache[key];
            if (entry && !this.valueExpired(entry)) {
                return entry.value;
            }
            return undefined;
        }
        set(key, value) {
            this._cache[key] = Object.assign({ value: value }, (this.ttl ? { expiresAt: Date.now() + this.ttl } : null));
        }
        remove(key) {
            delete this._cache[key];
        }
        getAll() {
            const allEntries = Object.values(this._cache);
            const res = [];
            return allEntries.reduce((res, entry) => {
                if (!this.valueExpired(entry)) {
                    res.push(entry.value);
                }
                return res;
            }, res);
        }
        keys() {
            return Object.keys(this._cache);
        }
        valueExpired(entry) {
            if (!entry.expiresAt) {
                return false;
            }
            return entry.expiresAt < Date.now();
        }
    }

    class CheckRequestStatus {
    }
    CheckRequestStatus.ALLOWED = 'ALLOWED';
    CheckRequestStatus.DENIED = 'DENIED';
    CheckRequestStatus.WAITING = 'WAITING';
    CheckRequestStatus.IDLE = 'IDLE';

    var PermissionErrorEnum;
    (function (PermissionErrorEnum) {
        PermissionErrorEnum["WEB_SDK_ERROR_LOCATION_PERMISSIONS_DENIED"] = "WEB_SDK_ERROR_LOCATION_PERMISSIONS_DENIED";
        PermissionErrorEnum["WEB_SDK_ERROR_LOCATION_PERMISSIONS_NOT_GIVEN"] = "WEB_SDK_ERROR_LOCATION_PERMISSIONS_NOT_GIVEN";
        PermissionErrorEnum["WEB_SDK_ERROR_LOCATION_PERMISSIONS_UNKNOWN"] = "WEB_SDK_ERROR_LOCATION_PERMISSIONS_UNKNOWN";
    })(PermissionErrorEnum || (PermissionErrorEnum = {}));

    var RequestType;
    (function (RequestType) {
        RequestType["FORCE"] = "FORCE";
        RequestType["INTERVAL"] = "INTERVAL";
        RequestType["FORCE_CHANGE_IP"] = "FORCE_CHANGE_IP";
        RequestType["BORDER_PROXIMITY"] = "BORDER_PROXIMITY";
        RequestType["WAGERING_START"] = "WAGERING_START";
        RequestType["JURISDICTION_AREA"] = "JURISDICTION_AREA";
    })(RequestType || (RequestType = {}));

    class SdkMetric {
    }
    SdkMetric.GPS_COLLECTION_TIME = 'GPS_COLLECTION_TIME';
    SdkMetric.TIME_TO_FIRST_CHECK = 'TIME_TO_FIRST_CHECK';
    SdkMetric.NUM_OF_ATTEMPTS = 'NUM_OF_ATTEMPTS';
    SdkMetric.INIT_REQUEST_RESPONSE_TIME = 'INIT_REQUEST_RESPONSE_TIME';
    SdkMetric.GPS_SEND_TIME = 'GPS_SEND_TIME';
    SdkMetric.DEVICEINFO_COLLECTION_TIME = 'DEVICEINFO_COLLECTION_TIME';
    SdkMetric.WIFI_COLLECTION_TIME = 'WIFI_COLLECTION_TIME';
    SdkMetric.APP_COLLECTION_TIME = 'APP_COLLECTION_TIME';
    SdkMetric.CELL_COLLECTION_TIME = 'CELL_COLLECTION_TIME';
    SdkMetric.OTHER_METRIC = 'OTHER_METRIC';
    SdkMetric.DEVICEINFO_SEND_TIME = 'DEVICEINFO_SEND_TIME';
    SdkMetric.WIFI_SEND_TIME = 'WIFI_SEND_TIME';
    SdkMetric.CELL_SEND_TIME = 'CELL_SEND_TIME';
    SdkMetric.APP_SEND_TIME = 'APP_SEND_TIME';
    SdkMetric.AVERAGE_FORCE_RESULT_TIME = 'AVERAGE_FORCE_RESULT_TIME';

    const buildUrlWithLoginOptions = (path, baseUrl, options) => {
        return buildUrl(path, baseUrl, options);
    };
    const buildWageringStartUrl = (baseUrl, options) => {
        return buildUrlWithLoginOptions('wagering-start', baseUrl, options);
    };
    const buildUrl = (path, baseUrl, options) => {
        const url = new URL(path, baseUrl);
        addQueryParamIfExist(url, 'userId', options === null || options === void 0 ? void 0 : options.userId);
        addQueryParamIfExist(url, 'client', options === null || options === void 0 ? void 0 : options.client);
        addQueryParamIfExist(url, 'customData', options === null || options === void 0 ? void 0 : options.customData);
        addQueryParamIfExist(url, 'clientBrand', options === null || options === void 0 ? void 0 : options.clientBrand);
        return url;
    };
    const wait = (seconds) => {
        return new Promise(resolve => {
            setTimeout(resolve, seconds * 1000, {});
        });
    };
    const addQueryParamIfExist = (url, name, value) => {
        if (value) {
            // https://stackoverflow.com/questions/51954827/decode-plus-sign-to-space-in-urlcomponent
            // url.searchParams.append(name, value);
            url.search += !url.search ? '?' : '&';
            url.search += `${name}=${encodeURIComponent(value)}`;
        }
    };

    var version = "4.2.0";

    const requestTimeout = 10000; // 10 seconds
    class FetchRequest {
        constructor(url, options) {
            this.url = url;
            this.options = options || {};
        }
        send(headersInterceptor) {
            const rejectAfterTimeout = new Promise(
            // @ts-ignore
            (resolve, reject) => setTimeout(reject, requestTimeout, new Error('Timeout error')));
            return Promise.race([this._send(headersInterceptor), rejectAfterTimeout]);
        }
        sendWithRetry(retryCount, retryTimeout, headersInterceptor) {
            return __awaiter(this, void 0, void 0, function* () {
                const rejectAfterTimeout = new Promise(
                // @ts-ignore
                (resolve, reject) => setTimeout(reject, requestTimeout, new Error('Timeout error')));
                return Promise.race([this._sendWithRetry(retryCount, retryTimeout, headersInterceptor), rejectAfterTimeout]);
            });
        }
        _send(headersInterceptor) {
            return fetch(this.url, this.options).then((response) => {
                if (response.ok) {
                    return new Promise((resolve, reject) => {
                        response.json()
                            .then(data => {
                            if (headersInterceptor) {
                                const extra = headersInterceptor(response.headers);
                                resolve(Object.assign(Object.assign({}, data), extra));
                            }
                            else {
                                resolve(data);
                            }
                        })
                            .catch(e => reject(e));
                    });
                }
                return response.json().then((data) => {
                    return Promise.reject({
                        status: response.status,
                        statusText: response.statusText,
                        message: (data === null || data === void 0 ? void 0 : data.message) || (data === null || data === void 0 ? void 0 : data.info),
                    });
                });
            });
        }
        _sendWithRetry(retryCount, retryTimeout, headersInterceptor) {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    return yield this.send(headersInterceptor);
                }
                catch (e) {
                    if (retryCount > 0) {
                        yield wait(retryTimeout);
                        return this.sendWithRetry(retryCount - 1, retryTimeout, headersInterceptor);
                    }
                    else {
                        throw e;
                    }
                }
            });
        }
    }

    // A Core Data timestamp is the number of seconds (or nanoseconds) since midnight, January 1, 2001, GMT
    // The difference between a Core Data timestamp and a Unix timestamp (seconds since 1/1/1970) is 978307200 seconds.
    const MAC_AND_UNIX_CORE_TIME_DIFF = 978307200000; // millis
    const NO_USER = 'no_user';
    const sendRequest = (path, body, apiKey, headersInterceptor) => {
        const headers = {
            'x-api-key': apiKey,
            'content-type': 'application/json',
        };
        return new FetchRequest(path, {
            method: 'POST',
            body,
            headers,
        }).sendWithRetry(1, 1, headersInterceptor);
    };
    const generateApiKey = (clientKey) => __awaiter(void 0, void 0, void 0, function* () {
        const message = `O50rhDrqW6POa1jIDAKK^${clientKey.length}${clientKey.toLowerCase()}`;
        const msgUint8 = new TextEncoder().encode(message);
        const hashBuffer = yield crypto.subtle.digest('SHA-1', msgUint8);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    });
    const coordToE6 = (latitude) => {
        return Math.round(latitude * 1e6);
    };
    const getCurrentPosition = (options) => __awaiter(void 0, void 0, void 0, function* () {
        const rejectAfterTimeout = new Promise(
        // @ts-ignore
        (resolve, reject) => setTimeout(reject, options.timeout, new Error(`Unable to get current geo position within timeout ${options.timeout}`)));
        return Promise.race([_tryToGetCurrentPosition(options), rejectAfterTimeout]);
    });
    const _tryToGetCurrentPosition = (options) => __awaiter(void 0, void 0, void 0, function* () {
        if (navigator.geolocation) {
            const position = yield new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, options));
            const timestamp = convertCocoaTimestampToUnixIfNeeded(position.timestamp);
            return Promise.resolve({
                coords: position.coords,
                timestamp,
            });
        }
        else {
            return new Promise(resolve => resolve(null));
        }
    });
    const getApiUrl = (host) => {
        const apiHost = host !== null && host !== void 0 ? host : localStorage.getItem('xpjs.apiHost');
        if (apiHost && /xp23.xpoint.tech$/i.test(apiHost)) {
            return `https://${apiHost}`;
        }
        return 'https://xp23.xpoint.tech';
    };
    const patchSession = (sessionData) => {
        const session = Object.assign(Object.assign({}, loadSession()), sessionData);
        saveSession(session);
    };
    const loadSession = () => {
        return loadFromStorage('xpjs.session');
    };
    const saveSession = (session) => {
        saveToStorage(session, 'xpjs.session');
    };
    const loadFromStorage = (key) => {
        return JSON.parse(localStorage.getItem(key) || '{}');
    };
    const saveToStorage = (data, key) => {
        localStorage.setItem(key, JSON.stringify(data));
    };
    const getDeviceInfo = () => {
        return {
            brand: 'UNKNOWN',
            model: 'UNKNOWN',
            osVersion: 'UNKNOWN',
        };
    };
    const positionToGpsItems = (userKey, pos, error) => {
        var _a, _b, _c;
        const gpsItems = pos
            ? [
                {
                    accuracy: pos.coords.accuracy,
                    altitude: (_a = pos.coords.altitude) !== null && _a !== void 0 ? _a : undefined,
                    altitudeAccuracy: (_b = pos.coords.altitudeAccuracy) !== null && _b !== void 0 ? _b : undefined,
                    speed: (_c = pos.coords.speed) !== null && _c !== void 0 ? _c : undefined,
                    timestamp: pos.timestamp,
                    latitudeE6: coordToE6(pos.coords.latitude),
                    longitudeE6: coordToE6(pos.coords.longitude),
                    adId: 'unknown',
                    provider: 'xpoint-lite',
                },
            ]
            : [];
        return Object.assign({ systemInfo: {
                platform: 'UNKNOWN',
                jsVersion: version,
            }, idType: 'UNKNOWN', rows: gpsItems, userKey }, (error !== undefined ? { errors: [error] } : null));
    };
    const positionToWebSdkEvent = (pos) => {
        return {
            location: {
                lat: pos.coords.latitude,
                lon: pos.coords.longitude,
                accuracy: pos.coords.accuracy,
            },
            timestamp: pos.timestamp,
        };
    };
    const getClientInfo = (userId, deviceId, requestType, checkPhase, customData, clientBrand) => {
        return Object.assign(Object.assign(Object.assign({ userId,
            deviceId, systemInfo: {
                platform: 'UNKNOWN',
                jsVersion: version,
            }, deviceInfo: getDeviceInfo() }, (customData ? { customData } : null)), (clientBrand ? { clientBrand } : null)), { requestType,
            checkPhase });
    };
    const isCocoaTimestamp = (timestamp) => {
        return timestamp < Date.now() - MAC_AND_UNIX_CORE_TIME_DIFF;
    };
    const convertCocoaTimestampToUnix = (cocoaTimestamp) => {
        return cocoaTimestamp + MAC_AND_UNIX_CORE_TIME_DIFF;
    };
    const convertCocoaTimestampToUnixIfNeeded = (timestamp) => {
        return isCocoaTimestamp(timestamp) ? convertCocoaTimestampToUnix(timestamp) : timestamp;
    };

    const defaultConfigUrl = 'https://routing.xpoint.tech';
    const validBaseRegex = /^[a-z0-9-]*$/;
    const CACHE_TTL = 15 * 60 * 1000; // 15 mins
    class RoutingService {
        constructor(options) {
            this.apiUrlByClientKey = new SdkCacheImpl(CACHE_TTL);
            this.options = options;
        }
        static isValidBase(base) {
            return validBaseRegex.test(base);
        }
        fetchApiUrl(clientKey) {
            var _a, _b, _c;
            return __awaiter(this, void 0, void 0, function* () {
                const cached = this.apiUrlByClientKey.get(clientKey);
                if (cached) {
                    return Promise.resolve(cached);
                }
                const defaultUrl = getApiUrl((_a = this.options) === null || _a === void 0 ? void 0 : _a.apiHost);
                try {
                    const configUrl = ((_b = this.options) === null || _b === void 0 ? void 0 : _b.configUrl) ? this.options.configUrl : defaultConfigUrl;
                    const conf = yield this._fetchConfig(configUrl, clientKey);
                    const base = (_c = conf === null || conf === void 0 ? void 0 : conf.application) === null || _c === void 0 ? void 0 : _c['base-entry'];
                    if (base) {
                        if (RoutingService.isValidBase(base)) {
                            const apiUrl = `https://${base}.xpoint.tech`;
                            this.apiUrlByClientKey.set(clientKey, apiUrl);
                            console.log(`Config: ${apiUrl}`);
                            return Promise.resolve(apiUrl);
                        }
                        else {
                            console.log(`Invalid base in config: ${base}`);
                        }
                    }
                }
                catch (e) {
                    // do nothing, use default config
                }
                this.apiUrlByClientKey.set(clientKey, defaultUrl);
                return Promise.resolve(defaultUrl);
            });
        }
        _fetchConfig(configUrl, clientKey) {
            const requestUrl = `${configUrl}/api/v1/clients/${clientKey}`;
            return fetch(requestUrl, {
                headers: {
                    accept: 'application/json, text/plain, */*',
                },
            }).then(response => {
                if (response.ok) {
                    return response.json();
                }
                console.log('Failed to fetch config, use default.');
                return Promise.reject();
            });
        }
    }

    var JobStatus;
    (function (JobStatus) {
        JobStatus["SCHEDULED"] = "SCHEDULED";
        JobStatus["IN_PROGRESS"] = "IN_PROGRESS";
        JobStatus["FAILED"] = "FAILED";
        JobStatus["COMPLETED"] = "COMPLETED";
    })(JobStatus || (JobStatus = {}));
    var CheckPhase;
    (function (CheckPhase) {
        CheckPhase["INITIAL"] = "INITIAL";
        CheckPhase["RECHECK"] = "RECHECK";
    })(CheckPhase || (CheckPhase = {}));
    var PingApiCode;
    (function (PingApiCode) {
        PingApiCode["FORCE"] = "FORCE";
        PingApiCode["FORCE_CHANGE_IP"] = "FORCE_CHANGE_IP";
        PingApiCode["INTERVAL"] = "INTERVAL";
    })(PingApiCode || (PingApiCode = {}));

    class WebSdkLiteImpl {
        constructor() { }
        gps(apiKey, apiUrl, data) {
            return __awaiter(this, void 0, void 0, function* () {
                return sendRequest(`${apiUrl}/gps`, JSON.stringify(data), apiKey);
            });
        }
        forceResult(apiKey, apiUrl, data, headersInterceptor) {
            return __awaiter(this, void 0, void 0, function* () {
                return sendRequest(`${apiUrl}/force-result`, JSON.stringify(data), apiKey, headersInterceptor);
            });
        }
        initForce(apiKey, apiUrl, data) {
            return sendRequest(`${apiUrl}/init-force`, JSON.stringify(data), String(apiKey));
        }
        checkStatus(apiKey, apiUrl, data, headersInterceptor) {
            return sendRequest(`${apiUrl}/check-status`, JSON.stringify(data), String(apiKey), headersInterceptor);
        }
        pingApi(apiKey, apiUrl, data) {
            return sendRequest(`${apiUrl}/pingapi`, JSON.stringify(data), String(apiKey));
        }
        sendMetrics(apiKey, apiUrl, request) {
            return sendRequest(`${apiUrl}/sdk-metrics`, JSON.stringify(request), String(apiKey));
        }
        addMissingRequest(apiKey, apiUrl, data) {
            return sendRequest(`${apiUrl}/missing-requests`, JSON.stringify(data), String(apiKey));
        }
        getJurisdictionArea(apiKey, apiUrl, request) {
            return __awaiter(this, void 0, void 0, function* () {
                return sendRequest(`${apiUrl}/jurisdiction-area`, JSON.stringify(request), String(apiKey));
            });
        }
        getJurisdictionAreaDictionary(apiKey, apiUrl, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                return sendRequest(`${apiUrl}/jurisdiction-area-dictionary`, JSON.stringify(Object.assign({}, (clientBrand ? { clientBrand } : null))), String(apiKey));
            });
        }
        getGameTypes(apiKey, apiUrl, jurisdictionArea, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                const request = Object.assign({ jurisdictionArea: jurisdictionArea }, (clientBrand ? { clientBrand } : null));
                return sendRequest(`${apiUrl}/game-types`, JSON.stringify(request), String(apiKey));
            });
        }
    }

    var _WebSdkLiteClientImpl_instances, _a$1, _WebSdkLiteClientImpl_session, _WebSdkLiteClientImpl_webSdkLite, _WebSdkLiteClientImpl_currentPosition, _WebSdkLiteClientImpl_watchPositionHandlerId, _WebSdkLiteClientImpl_routingService, _WebSdkLiteClientImpl_apiTokenCache, _WebSdkLiteClientImpl_lockFlag, _WebSdkLiteClientImpl_headersInterceptor, _WebSdkLiteClientImpl_init, _WebSdkLiteClientImpl__liteCheck, _WebSdkLiteClientImpl_sendSdkMetrics, _WebSdkLiteClientImpl_startSendingPingApi, _WebSdkLiteClientImpl_startWatchingPositionOnce, _WebSdkLiteClientImpl_isCurrentPositionValid, _WebSdkLiteClientImpl_positionUpdated, _WebSdkLiteClientImpl_getMostRecentPosition, _WebSdkLiteClientImpl_sendPingApi, _WebSdkLiteClientImpl_getCurrentClientInfo, _WebSdkLiteClientImpl_registerLocalError, _WebSdkLiteClientImpl_sendLocalErrors, _WebSdkLiteClientImpl_generateApiTokenIfNeeded, _WebSdkLiteClientImpl_lock, _WebSdkLiteClientImpl_unlock, _WebSdkLiteClientImpl_checkGeolocationPermissions;
    const XPOINT_JWT_HEADER_NAME$1 = 'xpoint-jwt';
    const MAX_LOCAL_ERRORS = 2;
    const NO_INTERNET_CONNECTION_ERROR = {
        code: 0,
        description: "We cannot verify that you're within a permitted area. To help us verify your location make sure your" +
            ' Location Services is turned on and that multiple WiFi connections are within range of your device. Please, address items above then try again.',
    };
    const SERVICE_OVERLOADED_ERROR = {
        code: 0,
        description: 'The service is temporarily overloaded. Please try again later.',
    };
    const defaultPositionMaximumAge = 20 * 1000; // 20 seconds, cache time to live
    const defaultPositionRetrieveTimeout = 60000; // 1 min - time to wait for retrieving position data from the device
    const defaultCheckStatusPositionRetrieveTimeout = 5 * 1000; // 5 seconds - time to wait for retrieving position data from the device
    const forceUpdatePositionTime = 15; // each 15 seconds update location data manually
    const defaultPositionEnableHighAccuracy = false;
    const defaultWatchPositionOptions = {
        enableHighAccuracy: defaultPositionEnableHighAccuracy,
        maximumAge: defaultPositionMaximumAge,
        timeout: defaultPositionRetrieveTimeout,
    };
    const defaultCheckStatusPositionOptions = {
        enableHighAccuracy: defaultPositionEnableHighAccuracy,
        maximumAge: defaultPositionMaximumAge,
        timeout: defaultCheckStatusPositionRetrieveTimeout,
    };
    class WebSdkLiteClientImpl {
        constructor(options) {
            _WebSdkLiteClientImpl_instances.add(this);
            _WebSdkLiteClientImpl_session.set(this, {});
            _WebSdkLiteClientImpl_webSdkLite.set(this, void 0);
            _WebSdkLiteClientImpl_currentPosition.set(this, void 0);
            _WebSdkLiteClientImpl_watchPositionHandlerId.set(this, null);
            _WebSdkLiteClientImpl_routingService.set(this, void 0);
            _WebSdkLiteClientImpl_apiTokenCache.set(this, new SdkCacheImpl());
            _WebSdkLiteClientImpl_lockFlag.set(this, false);
            this.positionValidityThreshold = defaultPositionMaximumAge;
            this.options = options;
            __classPrivateFieldSet(this, _WebSdkLiteClientImpl_routingService, new RoutingService(options), "f");
            __classPrivateFieldSet(this, _WebSdkLiteClientImpl_webSdkLite, new WebSdkLiteImpl(), "f");
            __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_init).call(this);
        }
        static getInstance(options) {
            if (!WebSdkLiteClientImpl.instance) {
                WebSdkLiteClientImpl.instance = new WebSdkLiteClientImpl(options);
            }
            return WebSdkLiteClientImpl.instance;
        }
        liteCheck(options) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!(options === null || options === void 0 ? void 0 : options.userId) || !(options === null || options === void 0 ? void 0 : options.client)) {
                    throw new IllegalArgumentError('client and userId should be defined.');
                }
                try {
                    yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_lock).call(this);
                    const checkRequest = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl__liteCheck).call(this, options);
                    return checkRequest;
                }
                catch (e) {
                    // @ts-ignore
                    if ((e === null || e === void 0 ? void 0 : e.status) === 401 || (e === null || e === void 0 ? void 0 : e.status) === 403) {
                        throw new ClientNotFoundError();
                    }
                    else {
                        throw e;
                    }
                }
                finally {
                    yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_unlock).call(this);
                }
            });
        }
        getJurisdictionArea(clientKey, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!clientKey) {
                    throw new IllegalArgumentError('clientKey should be defined.');
                }
                const apiKey = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_generateApiTokenIfNeeded).call(this, clientKey);
                const apiUrl = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_routingService, "f").fetchApiUrl(clientKey);
                const position = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_getMostRecentPosition).call(this);
                const deviceId = __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").deviceId || '';
                const userKey = Object.assign({ userId: NO_USER, deviceId }, (clientBrand ? { clientBrand } : null));
                const error = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_checkGeolocationPermissions).call(this);
                const gpsItems = positionToGpsItems(userKey, position, error);
                const request = Object.assign({ clientInfo: Object.assign(Object.assign({}, getClientInfo(NO_USER, String(__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").deviceId), RequestType.JURISDICTION_AREA, CheckPhase.INITIAL, '', clientBrand)), { requestId: v4() }), gpsItems: gpsItems.rows }, (error ? { errors: [error] } : null));
                try {
                    const jurisdictionArea = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_webSdkLite, "f").getJurisdictionArea(apiKey, apiUrl, request);
                    return jurisdictionArea;
                }
                catch (e) {
                    // @ts-ignore
                    if ((e === null || e === void 0 ? void 0 : e.status) === 401 || (e === null || e === void 0 ? void 0 : e.status) === 403) {
                        throw new ClientNotFoundError();
                    }
                    else {
                        throw e;
                    }
                }
            });
        }
        getJurisdictionAreaDictionary(clientKey, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!clientKey) {
                    throw new IllegalArgumentError('clientKey should be defined.');
                }
                const apiKey = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_generateApiTokenIfNeeded).call(this, clientKey);
                const apiUrl = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_routingService, "f").fetchApiUrl(clientKey);
                try {
                    const jurisdictionAreaDictionary = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_webSdkLite, "f").getJurisdictionAreaDictionary(apiKey, apiUrl, clientBrand);
                    return jurisdictionAreaDictionary;
                }
                catch (e) {
                    // @ts-ignore
                    if ((e === null || e === void 0 ? void 0 : e.status) === 401 || (e === null || e === void 0 ? void 0 : e.status) === 403) {
                        throw new ClientNotFoundError();
                    }
                    else {
                        throw e;
                    }
                }
            });
        }
        getGameTypes(jurisdictionArea, clientKey, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!jurisdictionArea || !clientKey) {
                    throw new IllegalArgumentError('clientKey and jurisdictionArea should be defined.');
                }
                const apiKey = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_generateApiTokenIfNeeded).call(this, clientKey);
                const apiUrl = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_routingService, "f").fetchApiUrl(clientKey);
                try {
                    return __classPrivateFieldGet(this, _WebSdkLiteClientImpl_webSdkLite, "f").getGameTypes(apiKey, apiUrl, jurisdictionArea, clientBrand);
                }
                catch (e) {
                    // @ts-ignore
                    if ((e === null || e === void 0 ? void 0 : e.status) === 401 || (e === null || e === void 0 ? void 0 : e.status) === 403) {
                        throw new ClientNotFoundError();
                    }
                    else {
                        throw e;
                    }
                }
            });
        }
        getCheckStatusPositionOptions() {
            var _b;
            return ((_b = this.options) === null || _b === void 0 ? void 0 : _b.checkStatusPositionOptions) || defaultCheckStatusPositionOptions;
        }
        getWatchPositionOptions() {
            var _b;
            return ((_b = this.options) === null || _b === void 0 ? void 0 : _b.watchPositionOptions) || defaultWatchPositionOptions;
        }
    }
    _a$1 = WebSdkLiteClientImpl, _WebSdkLiteClientImpl_session = new WeakMap(), _WebSdkLiteClientImpl_webSdkLite = new WeakMap(), _WebSdkLiteClientImpl_currentPosition = new WeakMap(), _WebSdkLiteClientImpl_watchPositionHandlerId = new WeakMap(), _WebSdkLiteClientImpl_routingService = new WeakMap(), _WebSdkLiteClientImpl_apiTokenCache = new WeakMap(), _WebSdkLiteClientImpl_lockFlag = new WeakMap(), _WebSdkLiteClientImpl_instances = new WeakSet(), _WebSdkLiteClientImpl_headersInterceptor = function _WebSdkLiteClientImpl_headersInterceptor(headers) {
        const jwt = headers.get(XPOINT_JWT_HEADER_NAME$1);
        return Object.assign({}, (jwt ? { jwt } : null));
    }, _WebSdkLiteClientImpl_init = function _WebSdkLiteClientImpl_init() {
        var _b, _c;
        // Ensure deviceId
        __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").deviceId = loadSession().deviceId;
        if (!__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").deviceId) {
            __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").deviceId = v4();
            patchSession({ deviceId: __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").deviceId });
        }
        const timeouts = [defaultPositionMaximumAge];
        const checkStatusPosMaxAge = (_b = this.getCheckStatusPositionOptions()) === null || _b === void 0 ? void 0 : _b.maximumAge;
        if (checkStatusPosMaxAge) {
            timeouts.push(checkStatusPosMaxAge);
        }
        const watchPosMaxAge = (_c = this.getWatchPositionOptions()) === null || _c === void 0 ? void 0 : _c.maximumAge;
        if (watchPosMaxAge) {
            timeouts.push(watchPosMaxAge);
        }
        this.positionValidityThreshold = Math.max(...timeouts);
        // Start sending data to the backend in background
        __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_startSendingPingApi).call(this);
        __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_startWatchingPositionOnce).call(this);
    }, _WebSdkLiteClientImpl__liteCheck = function _WebSdkLiteClientImpl__liteCheck(options) {
        return __awaiter(this, void 0, void 0, function* () {
            __classPrivateFieldSet(this, _WebSdkLiteClientImpl_session, Object.assign(Object.assign({ deviceId: __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").deviceId, userId: options.userId, client: options.client }, (options.customData ? { customData: options.customData } : null)), (options.clientBrand ? { clientBrand: options.clientBrand } : null)), "f");
            const startFullTime = Date.now();
            const apiUrl = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_routingService, "f").fetchApiUrl(options.client);
            const apiKey = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_generateApiTokenIfNeeded).call(this, options.client);
            const position = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_getMostRecentPosition).call(this);
            const endGpsCollectingTime = Date.now();
            const userId = __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").userId || '';
            const deviceId = __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").deviceId || '';
            const clientBrand = __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").clientBrand;
            const userKey = Object.assign(Object.assign({ userId,
                deviceId }, (clientBrand ? { clientBrand } : null)), (options.jurisdictionArea ? { jurisdictionArea: options.jurisdictionArea } : null));
            const error = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_checkGeolocationPermissions).call(this);
            const gpsItems = positionToGpsItems(userKey, position, error);
            // send request asynchronously, no need to wait for response
            __classPrivateFieldGet(this, _WebSdkLiteClientImpl_webSdkLite, "f").gps(apiKey, apiUrl, gpsItems).catch(e => console.log(e));
            __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_sendPingApi).call(this, apiKey, apiUrl, options.jurisdictionArea);
            const requestId = v4();
            let initForceResp;
            let forceResultResp;
            let endInitRequestResponseTime;
            let numOfAttempts = 1;
            try {
                initForceResp = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_webSdkLite, "f").initForce(apiKey, apiUrl, {
                    clientInfo: Object.assign(Object.assign(Object.assign({}, __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_getCurrentClientInfo).call(this)), { requestId, initialRequestId: requestId, requestType: RequestType.FORCE }), (options.jurisdictionArea ? { jurisdictionArea: options.jurisdictionArea } : null)),
                });
                endInitRequestResponseTime = Date.now();
                let timeout = 1; // pause between /init-force response and /force-request request = 1 sec.
                const retryLimit = 2 * 60 * 1000; // retry /force-result for 2 minutes (120 secs / timeout between queries);
                const increaseTimeoutAfter = 5 * 1000; // increase retry timeout after first 5 seconds
                const startTime = Date.now();
                while ((forceResultResp === null || forceResultResp === void 0 ? void 0 : forceResultResp.jobStatus) !== JobStatus.COMPLETED &&
                    (forceResultResp === null || forceResultResp === void 0 ? void 0 : forceResultResp.jobStatus) !== JobStatus.FAILED &&
                    Date.now() - startTime < retryLimit) {
                    yield wait(timeout);
                    if (Date.now() - startTime > increaseTimeoutAfter) {
                        timeout += 1;
                    }
                    const requestId = v4();
                    forceResultResp = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_webSdkLite, "f").forceResult(apiKey, apiUrl, {
                        jobId: initForceResp.jobId,
                        clientInfo: Object.assign(Object.assign(Object.assign({}, __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_getCurrentClientInfo).call(this)), { requestId, initialRequestId: requestId, requestType: RequestType.FORCE }), (options.jurisdictionArea ? { jurisdictionArea: options.jurisdictionArea } : null)),
                    }, __classPrivateFieldGet(WebSdkLiteClientImpl, _a$1, "m", _WebSdkLiteClientImpl_headersInterceptor));
                    numOfAttempts = numOfAttempts + 1;
                }
            }
            catch (e) {
                // @ts-ignore
                if ((e === null || e === void 0 ? void 0 : e.status) === 401 || (e === null || e === void 0 ? void 0 : e.status) === 403) {
                    throw new ClientNotFoundError();
                }
                // Network error. Stop game with location not available status;
                const checkRequestResult = {
                    status: CheckRequestStatus.DENIED,
                    errors: [NO_INTERNET_CONNECTION_ERROR],
                };
                __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_registerLocalError).call(this, NO_INTERNET_CONNECTION_ERROR);
                return checkRequestResult;
            }
            if (forceResultResp && forceResultResp.jobStatus === JobStatus.COMPLETED) {
                const endFullTime = Date.now();
                __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_sendSdkMetrics).call(this, apiKey, apiUrl, Object.assign({ fullTime: endFullTime - startFullTime, gpsCollectingTime: endGpsCollectingTime - startFullTime, checkRequestId: requestId, numOfAttempts: numOfAttempts }, (endInitRequestResponseTime ? { initRequestResponseTime: endInitRequestResponseTime - startFullTime } : null)));
                return Object.assign(Object.assign({}, forceResultResp.data), ((forceResultResp === null || forceResultResp === void 0 ? void 0 : forceResultResp.jwt) ? { jwt: forceResultResp === null || forceResultResp === void 0 ? void 0 : forceResultResp.jwt } : null));
            }
            else {
                const checkRequestResult = {
                    status: CheckRequestStatus.DENIED,
                    errors: [SERVICE_OVERLOADED_ERROR],
                };
                __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_registerLocalError).call(this, SERVICE_OVERLOADED_ERROR);
                return checkRequestResult;
            }
        });
    }, _WebSdkLiteClientImpl_sendSdkMetrics = function _WebSdkLiteClientImpl_sendSdkMetrics(apiKey, apiUrl, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const sdkMetricsRequest = {
                clientInfo: Object.assign(Object.assign({}, __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_getCurrentClientInfo).call(this)), { requestId: options.checkRequestId }),
                userId: String(__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").userId),
                deviceId: String(__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").deviceId),
                checkRequestId: options.checkRequestId,
                metrics: [
                    {
                        metric: SdkMetric.TIME_TO_FIRST_CHECK,
                        value: options.fullTime,
                    },
                    {
                        metric: SdkMetric.GPS_COLLECTION_TIME,
                        value: options.gpsCollectingTime,
                    },
                    {
                        metric: SdkMetric.NUM_OF_ATTEMPTS,
                        value: options.numOfAttempts,
                    },
                    ...(options.initRequestResponseTime
                        ? [
                            {
                                metric: SdkMetric.INIT_REQUEST_RESPONSE_TIME,
                                value: options.initRequestResponseTime,
                            },
                        ]
                        : []),
                ],
            };
            try {
                __classPrivateFieldGet(this, _WebSdkLiteClientImpl_webSdkLite, "f").sendMetrics(apiKey, apiUrl, sdkMetricsRequest);
            }
            catch (e) {
                // do nothing
            }
        });
    }, _WebSdkLiteClientImpl_startSendingPingApi = function _WebSdkLiteClientImpl_startSendingPingApi() {
        return __awaiter(this, void 0, void 0, function* () {
            while (true) {
                yield wait(60); // 1 minute
                try {
                    if (__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").userId && __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").client) {
                        const clientKey = __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").client;
                        const apiUrl = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_routingService, "f").fetchApiUrl(clientKey);
                        const apiKey = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_generateApiTokenIfNeeded).call(this, clientKey);
                        yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_sendPingApi).call(this, apiKey, apiUrl);
                    }
                }
                catch (e) {
                    // internet communication error
                    // game will be stopped in next interval check if failed
                }
            }
        });
    }, _WebSdkLiteClientImpl_startWatchingPositionOnce = function _WebSdkLiteClientImpl_startWatchingPositionOnce() {
        return __awaiter(this, void 0, void 0, function* () {
            // Check if browser supports geolocation and if not already started watching position
            if (__classPrivateFieldGet(this, _WebSdkLiteClientImpl_watchPositionHandlerId, "f") || !navigator.geolocation) {
                return;
            }
            const posOptions = this.getWatchPositionOptions();
            // Watch position change and store to this.currentPosition. Send to the backend the most relevant data when needed.
            __classPrivateFieldSet(this, _WebSdkLiteClientImpl_watchPositionHandlerId, navigator.geolocation.watchPosition(position => __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_positionUpdated).call(this, position), () => { }, posOptions), "f");
            // In case if watchPosition stopped watching (e.g. location service was disabled and re-enabled) we update
            // position additionally
            while (true) {
                if (!__classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_isCurrentPositionValid).call(this)) {
                    const position = yield getCurrentPosition(posOptions).catch((err) => {
                        console.warn(`ERROR: ${err.message}`);
                    });
                    if (position) {
                        __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_positionUpdated).call(this, position);
                    }
                }
                yield wait(forceUpdatePositionTime); // wait 15 secs
            }
        });
    }, _WebSdkLiteClientImpl_isCurrentPositionValid = function _WebSdkLiteClientImpl_isCurrentPositionValid() {
        var _b;
        return !!(((_b = __classPrivateFieldGet(this, _WebSdkLiteClientImpl_currentPosition, "f")) === null || _b === void 0 ? void 0 : _b.timestamp) && __classPrivateFieldGet(this, _WebSdkLiteClientImpl_currentPosition, "f").timestamp > Date.now() - this.positionValidityThreshold);
    }, _WebSdkLiteClientImpl_positionUpdated = function _WebSdkLiteClientImpl_positionUpdated(position) {
        const timestamp = convertCocoaTimestampToUnixIfNeeded(position.timestamp);
        __classPrivateFieldSet(this, _WebSdkLiteClientImpl_currentPosition, {
            coords: position.coords,
            timestamp,
        }, "f");
    }, _WebSdkLiteClientImpl_getMostRecentPosition = function _WebSdkLiteClientImpl_getMostRecentPosition() {
        return __awaiter(this, void 0, void 0, function* () {
            if (__classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_isCurrentPositionValid).call(this)) {
                return Promise.resolve(__classPrivateFieldGet(this, _WebSdkLiteClientImpl_currentPosition, "f"));
            }
            const checkStatusPosOptions = this.getCheckStatusPositionOptions();
            const position = yield getCurrentPosition(checkStatusPosOptions).catch((err) => {
                console.warn(`ERROR: ${err.message}`);
            });
            if (position) {
                __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_positionUpdated).call(this, position);
            }
            // if unable to quickly get location send last received location data
            return __classPrivateFieldGet(this, _WebSdkLiteClientImpl_currentPosition, "f");
        });
    }, _WebSdkLiteClientImpl_sendPingApi = function _WebSdkLiteClientImpl_sendPingApi(apiKey, apiUrl, jurisdictionArea) {
        return __awaiter(this, void 0, void 0, function* () {
            const pingApiResponse = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_webSdkLite, "f").pingApi(apiKey, apiUrl, {
                clientInfo: Object.assign(Object.assign(Object.assign({}, __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_getCurrentClientInfo).call(this)), { requestId: v4() }), (jurisdictionArea ? { jurisdictionArea } : null)),
            });
            yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_sendLocalErrors).call(this);
            return pingApiResponse;
        });
    }, _WebSdkLiteClientImpl_getCurrentClientInfo = function _WebSdkLiteClientImpl_getCurrentClientInfo(checkPhase = CheckPhase.INITIAL) {
        return getClientInfo(String(__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").userId), String(__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").deviceId), RequestType.FORCE, checkPhase, __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").customData, __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").clientBrand);
    }, _WebSdkLiteClientImpl_registerLocalError = function _WebSdkLiteClientImpl_registerLocalError(error) {
        if (!__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").localErrors) {
            __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").localErrors = [];
        }
        if (!__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").localErrors.some(nextError => nextError.code === error.code)) {
            __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").localErrors.push(Object.assign(Object.assign({}, error), { clientTimestamp: Date.now() }));
        }
    }, _WebSdkLiteClientImpl_sendLocalErrors = function _WebSdkLiteClientImpl_sendLocalErrors() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").localErrors && __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").localErrors.length > 0) {
                    const clientKey = __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").client;
                    if (clientKey) {
                        const apiUrl = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_routingService, "f").fetchApiUrl(clientKey);
                        const apiKey = yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_generateApiTokenIfNeeded).call(this, clientKey);
                        const limit = Math.min(__classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").localErrors.length, MAX_LOCAL_ERRORS);
                        for (let i = 0; i < limit; i++) {
                            const nextError = __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").localErrors[i];
                            yield __classPrivateFieldGet(this, _WebSdkLiteClientImpl_webSdkLite, "f").addMissingRequest(apiKey, apiUrl, {
                                clientInfo: Object.assign(Object.assign({}, __classPrivateFieldGet(this, _WebSdkLiteClientImpl_instances, "m", _WebSdkLiteClientImpl_getCurrentClientInfo).call(this)), { requestId: v4() }),
                                errorCode: 4000,
                                failureReason: nextError.description,
                                clientTimestamp: nextError.clientTimestamp || Date.now(),
                            });
                        }
                    }
                    __classPrivateFieldGet(this, _WebSdkLiteClientImpl_session, "f").localErrors = [];
                }
            }
            catch (e) {
                // do nothing
            }
        });
    }, _WebSdkLiteClientImpl_generateApiTokenIfNeeded = function _WebSdkLiteClientImpl_generateApiTokenIfNeeded(clientKey) {
        return __awaiter(this, void 0, void 0, function* () {
            let token = __classPrivateFieldGet(this, _WebSdkLiteClientImpl_apiTokenCache, "f").get(clientKey);
            if (!token) {
                token = yield generateApiKey(clientKey);
                __classPrivateFieldGet(this, _WebSdkLiteClientImpl_apiTokenCache, "f").set(clientKey, token);
            }
            return Promise.resolve(token);
        });
    }, _WebSdkLiteClientImpl_lock = function _WebSdkLiteClientImpl_lock() {
        return __awaiter(this, void 0, void 0, function* () {
            while (__classPrivateFieldGet(this, _WebSdkLiteClientImpl_lockFlag, "f")) {
                yield wait(0.1);
            }
            __classPrivateFieldSet(this, _WebSdkLiteClientImpl_lockFlag, true, "f");
        });
    }, _WebSdkLiteClientImpl_unlock = function _WebSdkLiteClientImpl_unlock() {
        return __awaiter(this, void 0, void 0, function* () {
            __classPrivateFieldSet(this, _WebSdkLiteClientImpl_lockFlag, false, "f");
        });
    }, _WebSdkLiteClientImpl_checkGeolocationPermissions = function _WebSdkLiteClientImpl_checkGeolocationPermissions() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const geoPermission = yield navigator.permissions.query({ name: 'geolocation' });
                if (geoPermission.state === 'denied') {
                    return PermissionErrorEnum.WEB_SDK_ERROR_LOCATION_PERMISSIONS_DENIED;
                }
                else if (geoPermission.state === 'prompt') {
                    return PermissionErrorEnum.WEB_SDK_ERROR_LOCATION_PERMISSIONS_NOT_GIVEN;
                }
            }
            catch (e) {
                return PermissionErrorEnum.WEB_SDK_ERROR_LOCATION_PERMISSIONS_UNKNOWN;
            }
            return undefined;
        });
    };

    const POSITION_OPTIONS = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
    };
    class WebSdkDaemon {
        constructor(webSdkClient, options, ...handlers) {
            this.activated = true;
            this.handlers = [];
            this.webSdkClient = webSdkClient;
            this.options = options;
            if (handlers) {
                this.handlers.push(...handlers);
            }
            this.startPolling();
            this.startSendingLocationData();
        }
        static timeoutByStatus(checkRequestStatus) {
            if (!checkRequestStatus) {
                return 1;
            }
            switch (checkRequestStatus) {
                case CheckRequestStatus.ALLOWED:
                case CheckRequestStatus.DENIED:
                case CheckRequestStatus.IDLE:
                    return 10;
                case CheckRequestStatus.WAITING:
                    return 1;
                default:
                    return 10;
            }
        }
        subscribe(handler) {
            this.handlers.push(handler);
        }
        unsubscribe(handler) {
            this.handlers = handler ? this.handlers.filter(next => next !== handler) : [];
            if (this.handlers.length === 0) {
                this.currentCheckRequest = undefined;
                this.webSdkClient.wageringEnd(this.options);
            }
        }
        destroy() {
            this.unsubscribe();
            this.activated = false;
        }
        startPolling() {
            var _a, _b;
            return __awaiter(this, void 0, void 0, function* () {
                while (this.activated) {
                    if (!document.hidden && this.handlers.length > 0) {
                        const checkRequest = yield this.checkStatus();
                        if ((checkRequest === null || checkRequest === void 0 ? void 0 : checkRequest.status) && checkRequest.status !== ((_a = this.currentCheckRequest) === null || _a === void 0 ? void 0 : _a.status)) {
                            this.currentCheckRequest = checkRequest;
                            this.notifyAll(checkRequest);
                        }
                    }
                    yield wait(WebSdkDaemon.timeoutByStatus((_b = this.currentCheckRequest) === null || _b === void 0 ? void 0 : _b.status));
                }
            });
        }
        startSendingLocationData() {
            return __awaiter(this, void 0, void 0, function* () {
                while (this.activated) {
                    if (!document.hidden && this.handlers.length > 0) {
                        try {
                            const pos = yield getCurrentPosition(POSITION_OPTIONS);
                            if (pos) {
                                const data = positionToWebSdkEvent(pos);
                                yield this.webSdkClient.sendEvent(this.options, data);
                            }
                        }
                        catch (e) {
                            // do nothing
                        }
                    }
                    yield wait(30);
                }
            });
        }
        checkStatus() {
            return __awaiter(this, void 0, void 0, function* () {
                let checkRequest;
                try {
                    checkRequest = yield this.webSdkClient.checkStatus(this.options);
                }
                catch (e) {
                    checkRequest = { status: CheckRequestStatus.IDLE };
                }
                return checkRequest;
            });
        }
        notifyAll(event) {
            this.handlers.forEach(handler => handler(event));
        }
    }

    class VerifyIsNotAvailableError extends Error {
        constructor() {
            super('Verify is not available error');
        }
    }

    const priorityOrigin = 'http://127.0.0.1';
    const fallbackOrigin = 'https://local.xpoint.tech';
    const httpPorts = [18354, 18355, 18356];
    const httpsPorts = [28354, 28355, 28356];
    const tryUrls = [...httpPorts.map(p => `${priorityOrigin}:${p}`), ...httpsPorts.map(p => `${fallbackOrigin}:${p}`)];
    const JA_ID_PARAM_NAME = 'jaId';
    const JA_NAME_PARAM_NAME = 'jaName';
    const VERSION_PARAM_NAME = 'v';
    const CLIENT_PARAM_NAME = 'client';
    const CLIENT_BRAND_PARAM_NAME = 'clientBrand';
    class WebSdkImpl {
        constructor() {
            this.baseURL = '';
            this.version = version;
        }
        static findLocalServiceUrl() {
            return __awaiter(this, void 0, void 0, function* () {
                let foundUrl;
                for (let i = 0; i < tryUrls.length; i++) {
                    try {
                        yield new FetchRequest(tryUrls[i] + '/status').send();
                        foundUrl = tryUrls[i];
                        break;
                    }
                    catch (e) {
                        // API is not available on this URL, try next one
                    }
                }
                if (!foundUrl) {
                    throw new VerifyIsNotAvailableError();
                }
                return foundUrl;
            });
        }
        ensureApiIsAvailable() {
            return __awaiter(this, void 0, void 0, function* () {
                if (!this.baseURL) {
                    this.baseURL = yield WebSdkImpl.findLocalServiceUrl();
                }
                return this.baseURL;
            });
        }
        health(options, sessionId) {
            return __awaiter(this, void 0, void 0, function* () {
                yield this.ensureApiIsAvailable();
                const url = buildUrlWithLoginOptions('status', this.baseURL, options);
                if (sessionId) {
                    url.searchParams.append('sessionId', sessionId);
                }
                return new FetchRequest(url.toString()).send();
            });
        }
        wageringStart(options, headersInterceptor, jurisdictionArea) {
            return __awaiter(this, void 0, void 0, function* () {
                yield this.ensureApiIsAvailable();
                const url = buildWageringStartUrl(this.baseURL, options);
                addQueryParamIfExist(url, VERSION_PARAM_NAME, this.version);
                if (jurisdictionArea) {
                    addQueryParamIfExist(url, JA_ID_PARAM_NAME, jurisdictionArea.id);
                    addQueryParamIfExist(url, JA_NAME_PARAM_NAME, jurisdictionArea.name);
                }
                return new FetchRequest(url.toString()).send(headersInterceptor);
            });
        }
        checkStatus(sessionId, headersInterceptor) {
            return __awaiter(this, void 0, void 0, function* () {
                yield this.ensureApiIsAvailable();
                const url = new URL(`${this.baseURL}/check-status`);
                if (sessionId) {
                    url.searchParams.append('sessionId', sessionId);
                }
                return new FetchRequest(url.toString()).send(headersInterceptor);
            });
        }
        wageringEnd(sessionId) {
            return __awaiter(this, void 0, void 0, function* () {
                yield this.ensureApiIsAvailable();
                const url = new URL(`${this.baseURL}/wagering-end`);
                if (sessionId) {
                    url.searchParams.append('sessionId', sessionId);
                }
                return new FetchRequest(url.toString()).send();
            });
        }
        sendEvent(event, sessionId) {
            return __awaiter(this, void 0, void 0, function* () {
                // send event only if Xpoint Verify launched
                if (this.baseURL) {
                    const url = new URL(`${this.baseURL}/send-event`);
                    Object.entries(event).forEach(([key, value]) => url.searchParams.append(key, JSON.stringify(value)));
                    if (sessionId) {
                        url.searchParams.append('sessionId', sessionId);
                    }
                    return new FetchRequest(url.toString()).send();
                }
                return Promise.resolve();
            });
        }
        getJurisdictionArea(client, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                yield this.ensureApiIsAvailable();
                const url = new URL(`${this.baseURL}/jurisdiction-area`);
                addQueryParamIfExist(url, VERSION_PARAM_NAME, this.version);
                addQueryParamIfExist(url, CLIENT_PARAM_NAME, client);
                addQueryParamIfExist(url, CLIENT_BRAND_PARAM_NAME, clientBrand);
                return new FetchRequest(url.toString()).send();
            });
        }
        getJurisdictionAreaDictionary(client, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                yield this.ensureApiIsAvailable();
                const url = new URL(`${this.baseURL}/jurisdiction-area-dictionary`);
                addQueryParamIfExist(url, VERSION_PARAM_NAME, this.version);
                addQueryParamIfExist(url, CLIENT_PARAM_NAME, client);
                addQueryParamIfExist(url, CLIENT_BRAND_PARAM_NAME, clientBrand);
                return new FetchRequest(url.toString()).send();
            });
        }
        getGameTypes(jurisdictionArea, client, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                yield this.ensureApiIsAvailable();
                const url = new URL(`${this.baseURL}/game-types`);
                addQueryParamIfExist(url, VERSION_PARAM_NAME, this.version);
                addQueryParamIfExist(url, CLIENT_PARAM_NAME, client);
                addQueryParamIfExist(url, CLIENT_BRAND_PARAM_NAME, clientBrand);
                addQueryParamIfExist(url, JA_ID_PARAM_NAME, jurisdictionArea.id);
                addQueryParamIfExist(url, JA_NAME_PARAM_NAME, jurisdictionArea.name);
                return new FetchRequest(url.toString()).send();
            });
        }
    }

    var _PersistedSdkCache_instances, _PersistedSdkCache_namespace, _PersistedSdkCache_storageName, _PersistedSdkCache_storage, _PersistedSdkCache_loadFromStore, _PersistedSdkCache_saveToStorage, _PersistedSdkCache_filterValidEntries;
    const LIMIT = 100; // store only last 100 records to limit store size
    class PersistedSdkCache {
        constructor(storageName, ttl) {
            _PersistedSdkCache_instances.add(this);
            _PersistedSdkCache_namespace.set(this, 'xpjs');
            _PersistedSdkCache_storageName.set(this, void 0);
            _PersistedSdkCache_storage.set(this, void 0);
            this._cache = {};
            __classPrivateFieldSet(this, _PersistedSdkCache_storageName, __classPrivateFieldGet(this, _PersistedSdkCache_namespace, "f") + '.' + storageName, "f");
            __classPrivateFieldSet(this, _PersistedSdkCache_storage, sessionStorage, "f");
            this.ttl = ttl;
            __classPrivateFieldGet(this, _PersistedSdkCache_instances, "m", _PersistedSdkCache_loadFromStore).call(this);
        }
        get(key) {
            const entry = this._cache[key];
            if (entry && !this.valueExpired(entry)) {
                return entry.value;
            }
            return undefined;
        }
        set(key, value) {
            this._cache[key] = Object.assign({ value: value, addedAt: Date.now() }, (this.ttl ? { expiresAt: Date.now() + this.ttl } : null));
            __classPrivateFieldGet(this, _PersistedSdkCache_instances, "m", _PersistedSdkCache_saveToStorage).call(this);
        }
        remove(key) {
            delete this._cache[key];
            __classPrivateFieldGet(this, _PersistedSdkCache_instances, "m", _PersistedSdkCache_saveToStorage).call(this);
        }
        getAll() {
            const allEntries = Object.values(this._cache);
            const res = [];
            return allEntries.reduce((res, entry) => {
                if (!this.valueExpired(entry)) {
                    res.push(entry.value);
                }
                return res;
            }, res);
        }
        keys() {
            return Object.keys(this._cache);
        }
        valueExpired(entry) {
            if (entry.expiresAt === undefined || entry.expiresAt === null) {
                return false;
            }
            return entry.expiresAt < Date.now();
        }
    }
    _PersistedSdkCache_namespace = new WeakMap(), _PersistedSdkCache_storageName = new WeakMap(), _PersistedSdkCache_storage = new WeakMap(), _PersistedSdkCache_instances = new WeakSet(), _PersistedSdkCache_loadFromStore = function _PersistedSdkCache_loadFromStore() {
        try {
            const loadedStr = __classPrivateFieldGet(this, _PersistedSdkCache_storage, "f").getItem(__classPrivateFieldGet(this, _PersistedSdkCache_storageName, "f"));
            if (loadedStr) {
                try {
                    const loadedJson = JSON.parse(loadedStr);
                    this._cache = __classPrivateFieldGet(this, _PersistedSdkCache_instances, "m", _PersistedSdkCache_filterValidEntries).call(this, loadedJson);
                }
                catch (e) {
                    // do nothing
                }
            }
        }
        catch (e) {
            // SecurityError, QuotaExceededError
        }
    }, _PersistedSdkCache_saveToStorage = function _PersistedSdkCache_saveToStorage() {
        try {
            const toSave = __classPrivateFieldGet(this, _PersistedSdkCache_instances, "m", _PersistedSdkCache_filterValidEntries).call(this, this._cache);
            __classPrivateFieldGet(this, _PersistedSdkCache_storage, "f").setItem(__classPrivateFieldGet(this, _PersistedSdkCache_storageName, "f"), JSON.stringify(toSave));
        }
        catch (e) {
            // SecurityError, QuotaExceededError
        }
    }, _PersistedSdkCache_filterValidEntries = function _PersistedSdkCache_filterValidEntries(state) {
        const res = {};
        try {
            if (state) {
                const entries = Object.entries(state)
                    .sort(([, a], [, b]) => {
                    if (!(b === null || b === void 0 ? void 0 : b.addedAt) || ((a === null || a === void 0 ? void 0 : a.addedAt) && (b === null || b === void 0 ? void 0 : b.addedAt) && (a === null || a === void 0 ? void 0 : a.addedAt) > (b === null || b === void 0 ? void 0 : b.addedAt))) {
                        return -1;
                    }
                    if (!(a === null || a === void 0 ? void 0 : a.addedAt) || ((a === null || a === void 0 ? void 0 : a.addedAt) && (b === null || b === void 0 ? void 0 : b.addedAt) && (a === null || a === void 0 ? void 0 : a.addedAt) < (b === null || b === void 0 ? void 0 : b.addedAt))) {
                        return 1;
                    }
                    return 0;
                })
                    .slice(0, LIMIT);
                entries.reduce((res, [key, entry]) => {
                    if ((entry === null || entry === void 0 ? void 0 : entry.value) && !this.valueExpired(entry)) {
                        res[key] = Object.assign({ value: entry.value, addedAt: entry.addedAt }, (entry.expiresAt !== undefined && entry.expiresAt !== null ? { expiresAt: entry.expiresAt } : null));
                    }
                    return res;
                }, res);
            }
        }
        catch (e) {
            // do nothing
        }
        return res;
    };

    var _WebSdkClientImpl_instances, _a, _WebSdkClientImpl_webSdk, _WebSdkClientImpl_sessionsCache, _WebSdkClientImpl_locksCache, _WebSdkClientImpl_daemonsCache, _WebSdkClientImpl_headersInterceptor, _WebSdkClientImpl_optionsToString, _WebSdkClientImpl_toOptions, _WebSdkClientImpl__checkVerifyStatus, _WebSdkClientImpl_restartSession, _WebSdkClientImpl_lock, _WebSdkClientImpl_unlock;
    const XPOINT_JWT_HEADER_NAME = 'xpoint-jwt';
    const XPOINT_SESSION_ID_HEADER_NAME = 'xpoint-session-id';
    const LOCK_CACHE_TTL = 10 * 1000; // 10 seconds
    class WebSdkClientImpl {
        constructor() {
            _WebSdkClientImpl_instances.add(this);
            _WebSdkClientImpl_webSdk.set(this, void 0);
            _WebSdkClientImpl_sessionsCache.set(this, new PersistedSdkCache('sessions'));
            _WebSdkClientImpl_locksCache.set(this, new SdkCacheImpl(LOCK_CACHE_TTL));
            _WebSdkClientImpl_daemonsCache.set(this, new SdkCacheImpl());
            __classPrivateFieldSet(this, _WebSdkClientImpl_webSdk, new WebSdkImpl(), "f");
        }
        static getInstance() {
            if (!WebSdkClientImpl.instance) {
                WebSdkClientImpl.instance = new WebSdkClientImpl();
            }
            return WebSdkClientImpl.instance;
        }
        checkStatus(options) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!(options === null || options === void 0 ? void 0 : options.client) || !(options === null || options === void 0 ? void 0 : options.userId)) {
                    throw new IllegalArgumentError('client and userId should be defined.');
                }
                const key = __classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_optionsToString).call(WebSdkClientImpl, options);
                try {
                    yield __classPrivateFieldGet(this, _WebSdkClientImpl_instances, "m", _WebSdkClientImpl_lock).call(this, key);
                    const checkRequest = yield __classPrivateFieldGet(this, _WebSdkClientImpl_instances, "m", _WebSdkClientImpl__checkVerifyStatus).call(this, options);
                    return checkRequest;
                }
                catch (e) {
                    // @ts-ignore
                    if ((e === null || e === void 0 ? void 0 : e.status) === 401) {
                        throw new ClientNotFoundError();
                    }
                    else {
                        throw new VerifyIsNotAvailableError();
                    }
                }
                finally {
                    yield __classPrivateFieldGet(this, _WebSdkClientImpl_instances, "m", _WebSdkClientImpl_unlock).call(this, key);
                }
            });
        }
        wageringEnd(options) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!(options === null || options === void 0 ? void 0 : options.client) || !(options === null || options === void 0 ? void 0 : options.userId)) {
                    throw new IllegalArgumentError('client and userId should be defined.');
                }
                const key = __classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_optionsToString).call(WebSdkClientImpl, options);
                try {
                    yield __classPrivateFieldGet(this, _WebSdkClientImpl_instances, "m", _WebSdkClientImpl_lock).call(this, key);
                    const sessionId = __classPrivateFieldGet(this, _WebSdkClientImpl_sessionsCache, "f").get(key);
                    if (sessionId) {
                        yield __classPrivateFieldGet(this, _WebSdkClientImpl_webSdk, "f").wageringEnd(sessionId);
                        __classPrivateFieldGet(this, _WebSdkClientImpl_sessionsCache, "f").remove(key);
                    }
                }
                catch (e) {
                    // do nothing
                }
                finally {
                    yield __classPrivateFieldGet(this, _WebSdkClientImpl_instances, "m", _WebSdkClientImpl_unlock).call(this, key);
                }
            });
        }
        sendEvent(options, event) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!(options === null || options === void 0 ? void 0 : options.client) || !(options === null || options === void 0 ? void 0 : options.userId)) {
                    throw new IllegalArgumentError('client and userId should be defined.');
                }
                const key = __classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_optionsToString).call(WebSdkClientImpl, options);
                const sessionId = __classPrivateFieldGet(this, _WebSdkClientImpl_sessionsCache, "f").get(key);
                if (sessionId) {
                    try {
                        __classPrivateFieldGet(this, _WebSdkClientImpl_webSdk, "f").sendEvent(event, sessionId);
                    }
                    catch (e) {
                        // do nothing
                    }
                }
            });
        }
        subscribe(options, handler) {
            if (!(options === null || options === void 0 ? void 0 : options.client) || !(options === null || options === void 0 ? void 0 : options.userId) || !handler) {
                throw new IllegalArgumentError('client, userId, handler should be defined.');
            }
            const key = __classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_optionsToString).call(WebSdkClientImpl, options);
            let daemon = __classPrivateFieldGet(this, _WebSdkClientImpl_daemonsCache, "f").get(key);
            if (!daemon) {
                daemon = new WebSdkDaemon(this, options, handler);
                __classPrivateFieldGet(this, _WebSdkClientImpl_daemonsCache, "f").set(key, daemon);
            }
            else {
                daemon.subscribe(handler);
            }
        }
        unsubscribe(options, handler) {
            if (options) {
                if (!(options === null || options === void 0 ? void 0 : options.client) || !(options === null || options === void 0 ? void 0 : options.userId)) {
                    throw new IllegalArgumentError('client and userId should be defined.');
                }
                const key = __classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_optionsToString).call(WebSdkClientImpl, options);
                let daemon = __classPrivateFieldGet(this, _WebSdkClientImpl_daemonsCache, "f").get(key);
                if (daemon) {
                    daemon.unsubscribe(handler);
                }
                else {
                    // terminate session in Verify app explicitly if daemon was not created
                    this.wageringEnd(options);
                }
            }
            else {
                const daemons = __classPrivateFieldGet(this, _WebSdkClientImpl_daemonsCache, "f").getAll();
                daemons.forEach(next => next.unsubscribe());
                // terminate all sessions in Verify app explicitly for which daemons were not created
                const sessionsKeys = __classPrivateFieldGet(this, _WebSdkClientImpl_sessionsCache, "f").keys();
                const daemonsKeys = __classPrivateFieldGet(this, _WebSdkClientImpl_daemonsCache, "f").keys();
                sessionsKeys.forEach(key => {
                    if (!daemonsKeys.includes(key)) {
                        this.wageringEnd(__classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_toOptions).call(WebSdkClientImpl, key));
                    }
                });
            }
        }
        getJurisdictionArea(clientKey, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!clientKey) {
                    throw new IllegalArgumentError('clientKey should be defined');
                }
                try {
                    const jurisdictionArea = yield __classPrivateFieldGet(this, _WebSdkClientImpl_webSdk, "f").getJurisdictionArea(clientKey, clientBrand);
                    return jurisdictionArea;
                }
                catch (e) {
                    // @ts-ignore
                    if ((e === null || e === void 0 ? void 0 : e.status) === 401) {
                        throw new ClientNotFoundError();
                    }
                    else {
                        throw new VerifyIsNotAvailableError();
                    }
                }
            });
        }
        getJurisdictionAreaDictionary(clientKey, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!clientKey) {
                    throw new IllegalArgumentError('clientKey should be defined');
                }
                try {
                    const jurisdictionAreaDictionary = yield __classPrivateFieldGet(this, _WebSdkClientImpl_webSdk, "f").getJurisdictionAreaDictionary(clientKey, clientBrand);
                    return jurisdictionAreaDictionary;
                }
                catch (e) {
                    // @ts-ignore
                    if ((e === null || e === void 0 ? void 0 : e.status) === 401) {
                        throw new ClientNotFoundError();
                    }
                    else {
                        throw new VerifyIsNotAvailableError();
                    }
                }
            });
        }
        getGameTypes(jurisdictionArea, clientKey, clientBrand) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!jurisdictionArea || !clientKey) {
                    throw new IllegalArgumentError('clientKey and jurisdictionArea should be defined');
                }
                try {
                    const gameTypes = yield __classPrivateFieldGet(this, _WebSdkClientImpl_webSdk, "f").getGameTypes(jurisdictionArea, clientKey, clientBrand);
                    return gameTypes;
                }
                catch (e) {
                    // @ts-ignore
                    if ((e === null || e === void 0 ? void 0 : e.status) === 401) {
                        throw new ClientNotFoundError();
                    }
                    else {
                        throw new VerifyIsNotAvailableError();
                    }
                }
            });
        }
        isVerifyActive() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    yield __classPrivateFieldGet(this, _WebSdkClientImpl_webSdk, "f").health();
                }
                catch (e) {
                    return false;
                }
                return true;
            });
        }
    }
    _a = WebSdkClientImpl, _WebSdkClientImpl_webSdk = new WeakMap(), _WebSdkClientImpl_sessionsCache = new WeakMap(), _WebSdkClientImpl_locksCache = new WeakMap(), _WebSdkClientImpl_daemonsCache = new WeakMap(), _WebSdkClientImpl_instances = new WeakSet(), _WebSdkClientImpl_headersInterceptor = function _WebSdkClientImpl_headersInterceptor(headers) {
        const jwt = headers.get(XPOINT_JWT_HEADER_NAME);
        const sessionId = headers.get(XPOINT_SESSION_ID_HEADER_NAME);
        return Object.assign(Object.assign({}, (jwt ? { jwt } : null)), (sessionId ? { sessionId } : null));
    }, _WebSdkClientImpl_optionsToString = function _WebSdkClientImpl_optionsToString(options) {
        return JSON.stringify(options, Object.keys(options).sort());
    }, _WebSdkClientImpl_toOptions = function _WebSdkClientImpl_toOptions(key) {
        return JSON.parse(key);
    }, _WebSdkClientImpl__checkVerifyStatus = function _WebSdkClientImpl__checkVerifyStatus(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const key = __classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_optionsToString).call(WebSdkClientImpl, options);
            let sessionId = __classPrivateFieldGet(this, _WebSdkClientImpl_sessionsCache, "f").get(key);
            if (!sessionId) {
                // start new session if there is no active session
                sessionId = yield __classPrivateFieldGet(this, _WebSdkClientImpl_instances, "m", _WebSdkClientImpl_restartSession).call(this, options);
                __classPrivateFieldGet(this, _WebSdkClientImpl_sessionsCache, "f").set(key, sessionId);
            }
            let checkRequest;
            try {
                checkRequest = yield __classPrivateFieldGet(this, _WebSdkClientImpl_webSdk, "f").checkStatus(sessionId, __classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_headersInterceptor));
                if (checkRequest.status === CheckRequestStatus.IDLE) {
                    // restart session if Verify is in IDLE status (session is terminated after 15/30 minutes timout)
                    sessionId = yield __classPrivateFieldGet(this, _WebSdkClientImpl_instances, "m", _WebSdkClientImpl_restartSession).call(this, options);
                    __classPrivateFieldGet(this, _WebSdkClientImpl_sessionsCache, "f").set(key, sessionId);
                    checkRequest = yield __classPrivateFieldGet(this, _WebSdkClientImpl_webSdk, "f").checkStatus(sessionId, __classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_headersInterceptor));
                }
            }
            catch (e) {
                // restart session if Verify has no active session and check status returned 404 or another error
                sessionId = yield __classPrivateFieldGet(this, _WebSdkClientImpl_instances, "m", _WebSdkClientImpl_restartSession).call(this, options);
                __classPrivateFieldGet(this, _WebSdkClientImpl_sessionsCache, "f").set(key, sessionId);
                checkRequest = yield __classPrivateFieldGet(this, _WebSdkClientImpl_webSdk, "f").checkStatus(sessionId, __classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_headersInterceptor));
            }
            return checkRequest;
        });
    }, _WebSdkClientImpl_restartSession = function _WebSdkClientImpl_restartSession(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const session = yield __classPrivateFieldGet(this, _WebSdkClientImpl_webSdk, "f").wageringStart(options, __classPrivateFieldGet(WebSdkClientImpl, _a, "m", _WebSdkClientImpl_headersInterceptor), options.jurisdictionArea);
            if (!(session === null || session === void 0 ? void 0 : session.sessionId)) {
                throw Error('Failed to start new session');
            }
            return session.sessionId;
        });
    }, _WebSdkClientImpl_lock = function _WebSdkClientImpl_lock(key) {
        return __awaiter(this, void 0, void 0, function* () {
            while (__classPrivateFieldGet(this, _WebSdkClientImpl_locksCache, "f").get(key)) {
                yield wait(0.1);
            }
            __classPrivateFieldGet(this, _WebSdkClientImpl_locksCache, "f").set(key, { status: CheckRequestStatus.WAITING });
        });
    }, _WebSdkClientImpl_unlock = function _WebSdkClientImpl_unlock(key) {
        return __awaiter(this, void 0, void 0, function* () {
            __classPrivateFieldGet(this, _WebSdkClientImpl_locksCache, "f").remove(key);
        });
    };

    class GameType {
    }
    GameType.DFS = 'DFS';
    GameType.CASINO = 'CASINO';
    GameType.BETTING = 'BETTING';

    exports.CheckRequestStatus = CheckRequestStatus;
    exports.ClientNotFoundError = ClientNotFoundError;
    exports.GameType = GameType;
    exports.IllegalArgumentError = IllegalArgumentError;
    exports.VerifyIsNotAvailableError = VerifyIsNotAvailableError;
    exports.WebSdk = WebSdkClientImpl;
    exports.WebSdkLite = WebSdkLiteClientImpl;

}));
